import { pick } from 'lodash';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { PhylloApi } from '../../util/api';
import { StatusCode } from '../../util/enums';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';

// ================ Action types ================ //

const ActionTypes = {
  SET_INITIAL_VALUES: 'app/PhylloConnectPage/SET_INITIAL_VALUES',

  CREATE_USER_REQUEST: 'app/PhylloConnectPage/CREATE_USER_REQUEST',
  CREATE_USER_SUCCESS: 'app/PhylloConnectPage/CREATE_USER_SUCCESS',
  CREATE_USER_ERROR: 'app/PhylloConnectPage/CREATE_USER_ERROR',

  GENERATE_TOKEN_REQUEST: 'app/PhylloConnectPage/GENERATE_TOKEN_REQUEST',
  GENERATE_TOKEN_SUCCESS: 'app/PhylloConnectPage/GENERATE_TOKEN_SUCCESS',
  GENERATE_TOKEN_ERROR: 'app/PhylloConnectPage/GENERATE_TOKEN_ERROR',

  FETCH_USER_DATA_REQUEST: 'app/PhylloConnectPage/FETCH_USER_DATA_REQUEST',
  FETCH_USER_DATA_SUCCESS: 'app/PhylloConnectPage/FETCH_USER_DATA_SUCCESS',
  FETCH_USER_DATA_ERROR: 'app/PhylloConnectPage/FETCH_USER_DATA_ERROR',

  FETCH_USER_ACCOUNTS_REQUEST: 'app/PhylloConnectPage/FETCH_USER_ACCOUNTS_REQUEST',
  FETCH_USER_ACCOUNTS_SUCCESS: 'app/PhylloConnectPage/FETCH_USER_ACCOUNTS_SUCCESS',
  FETCH_USER_ACCOUNTS_ERROR: 'app/PhylloConnectPage/FETCH_USER_ACCOUNTS_ERROR',

  DISCONNECT_ACCOUNT_REQUEST: 'app/PhylloConnectPage/DISCONNECT_ACCOUNT_REQUEST',
  DISCONNECT_ACCOUNT_SUCCESS: 'app/PhylloConnectPage/DISCONNECT_ACCOUNT_SUCCESS',
  DISCONNECT_ACCOUNT_ERROR: 'app/PhylloConnectPage/DISCONNECT_ACCOUNT_ERROR',
};

// ================ Initial State ================ //

const initialState = {
  createUserInProgress: false,
  createUserError: null,
  generateTokenInProgress: false,
  generateTokenError: null,
  phylloUserId: null,
  sdkToken: null,
  fetchUserDataInProgress: false,
  fetchUserDataError: null,
  userData: [],
  fetchUserAccountsInProgress: false,
  fetchUserAccountsError: null,
  connectedAccounts: [],
  disconnectAccountInProgress: false,
  disconnectAccountError: null,
};

// ================ Reducers ================ //

const PhylloConnectPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case ActionTypes.CREATE_USER_REQUEST:
      return { ...state, createUserError: null, createUserInProgress: true };
    case ActionTypes.CREATE_USER_SUCCESS:
      return {
        ...state,
        createUserError: null,
        createUserInProgress: false,
        phylloUserId: payload,
      };
    case ActionTypes.CREATE_USER_ERROR:
      return { ...state, createUserError: payload, createUserInProgress: false };

    case ActionTypes.GENERATE_TOKEN_REQUEST:
      return { ...state, generateTokenError: null, generateTokenInProgress: true };
    case ActionTypes.GENERATE_TOKEN_SUCCESS:
      return {
        ...state,
        generateTokenError: null,
        generateTokenInProgress: false,
        sdkToken: payload,
      };
    case ActionTypes.GENERATE_TOKEN_ERROR:
      return { ...state, generateTokenError: payload, generateTokenInProgress: false };

    case ActionTypes.FETCH_USER_DATA_REQUEST:
      return { ...state, fetchUserDataError: null, fetchUserDataInProgress: true };
    case ActionTypes.FETCH_USER_DATA_SUCCESS:
      return {
        ...state,
        fetchUserDataError: null,
        fetchUserDataInProgress: false,
        userData: payload,
      };
    case ActionTypes.FETCH_USER_DATA_ERROR:
      return { ...state, fetchUserDataError: payload, fetchUserDataInProgress: false };

    case ActionTypes.FETCH_USER_ACCOUNTS_REQUEST:
      return { ...state, fetchUserAccountsError: null, fetchUserAccountsInProgress: true };
    case ActionTypes.FETCH_USER_ACCOUNTS_SUCCESS:
      return {
        ...state,
        fetchUserAccountsError: null,
        fetchUserAccountsInProgress: false,
        connectedAccounts: payload,
      };
    case ActionTypes.FETCH_USER_ACCOUNTS_ERROR:
      return { ...state, fetchUserAccountsError: payload, fetchUserAccountsInProgress: false };

    case ActionTypes.DISCONNECT_ACCOUNT_REQUEST:
      return { ...state, disconnectAccountError: null, disconnectAccountInProgress: true };
    case ActionTypes.DISCONNECT_ACCOUNT_SUCCESS:
      return {
        ...state,
        disconnectAccountError: null,
        disconnectAccountInProgress: false,
      };
    case ActionTypes.DISCONNECT_ACCOUNT_ERROR:
      return { ...state, disconnectAccountError: payload, disconnectAccountInProgress: false };
    default:
      return state;
  }
};

// ================ Selectors ================ //

export const createUserInProgressSelector = state => state.PhylloConnectPage.createUserInProgress;
export const generateTokenInProgressSelector = state =>
  state.PhylloConnectPage.generateTokenInProgress;
export const phylloUserIdSelector = state => state.PhylloConnectPage.phylloUserId;
export const sdkTokenSelector = state => state.PhylloConnectPage.sdkToken;
export const disconnectInProgressSelector = state =>
  state.PhylloConnectPage.disconnectAccountInProgress;
export const fetchUserDataInProgressSelector = state =>
  state.PhylloConnectPage.fetchUserDataInProgress;
export const userDataSelector = state => state.PhylloConnectPage.userData;
export const connectedAccountsSelector = state => state.PhylloConnectPage.connectedAccounts;

// ================ Actions Creators ================ //
export const setInitialValues = initialValues => ({
  type: ActionTypes.SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const createUserRequest = () => ({ type: ActionTypes.CREATE_USER_REQUEST });
const createUserSuccess = userId => ({ type: ActionTypes.CREATE_USER_SUCCESS, payload: userId });
const createUserError = error => ({
  type: ActionTypes.CREATE_USER_ERROR,
  error: true,
  payload: error,
});

const generateTokenRequest = () => ({ type: ActionTypes.GENERATE_TOKEN_REQUEST });
const generateTokenSuccess = sdkToken => ({
  type: ActionTypes.GENERATE_TOKEN_SUCCESS,
  payload: sdkToken,
});
const generateTokenError = error => ({
  type: ActionTypes.GENERATE_TOKEN_ERROR,
  error: true,
  payload: error,
});

const fetchUserDataRequest = () => ({ type: ActionTypes.FETCH_USER_DATA_REQUEST });
const fetchUserDataSuccess = userData => ({
  type: ActionTypes.FETCH_USER_DATA_SUCCESS,
  payload: userData,
});
const fetchUserDataError = error => ({
  type: ActionTypes.FETCH_USER_DATA_ERROR,
  error: true,
  payload: error,
});

const fetchUserAccountsRequest = () => ({ type: ActionTypes.FETCH_USER_ACCOUNTS_REQUEST });
const fetchUserAccountsSuccess = connectedAccounts => ({
  type: ActionTypes.FETCH_USER_ACCOUNTS_SUCCESS,
  payload: connectedAccounts,
});
const fetchUserAccountsError = error => ({
  type: ActionTypes.FETCH_USER_ACCOUNTS_ERROR,
  error: true,
  payload: error,
});

const disconnectAccountRequest = () => ({ type: ActionTypes.DISCONNECT_ACCOUNT_REQUEST });
const disconnectAccountSuccess = () => ({ type: ActionTypes.DISCONNECT_ACCOUNT_SUCCESS });
const disconnectAccountError = error => ({
  type: ActionTypes.DISCONNECT_ACCOUNT_ERROR,
  error: true,
  payload: error,
});
// ================ Thunks ================ //

export const createUser = params => async (dispatch, getState, sdk) => {
  dispatch(createUserRequest());
  try {
    const response = await PhylloApi.createUser(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(createUserSuccess(response.data.id));
      return response.data.id;
    }
  } catch (e) {
    dispatch(createUserError(storableError(e)));
  }
};

export const generateToken = params => async (dispatch, getState, sdk) => {
  dispatch(generateTokenRequest());
  try {
    const response = await PhylloApi.generateToken(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(generateTokenSuccess(response.data.sdk_token));
      return response.data.sdk_token;
    }
  } catch (e) {
    dispatch(generateTokenError(storableError(e)));
  }
};
export const fetchUserAccounts = params => async (dispatch, getState, sdk) => {
  dispatch(fetchUserAccountsRequest());
  try {
    const response = await PhylloApi.getUserAccounts(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchUserAccountsSuccess(response.data.data)); // Assuming response.data.data is the array of accounts
      return response.data.data;
    }
  } catch (e) {
    dispatch(fetchUserAccountsError(storableError(e)));
  }
};
export const fetchUserData = params => async (dispatch, getState, sdk) => {
  dispatch(fetchUserDataRequest());
  try {
    const response = await PhylloApi.getUserData(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchUserDataSuccess(response.data));
      return response.data;
    }
  } catch (e) {
    dispatch(fetchUserDataError(storableError(e)));
  }
};

export const disconnectAccount = params => async (dispatch, getState, sdk) => {
  dispatch(disconnectAccountRequest());
  try {
    const response = await PhylloApi.disconnectAccount(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(disconnectAccountSuccess(response.data));
      dispatch(setInitialValues({ userData: [] }));

      return response.data;
    }
  } catch (e) {
    dispatch(disconnectAccountError(storableError(e)));
  }
};

// Thunks
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
  const currentUserId = currentUser?.id;

  return currentUserId;
};

export default PhylloConnectPageReducer;
